import { useCallback, useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import { getSendBeforeClearing, sendBeforeClearing } from '@/services/documentSends.service.js';
import { announceDocumentsChanged } from '@/services/deals.service.js';
import { CcField, CcResult, DocumentLine, MissingItem, ccList, sendWord } from './sendSheetParts.jsx';
import SendEndorsementButton from './SendEndorsementButton.jsx';

// "Payment processing: send now" (paperwork-1 round 2, David 2026-10-31 Q24: "send on clearing, plus a 'Payment processing:
// send now' button for urgent cases (logged)"). Shown on an endorsement row whose Nickel payment is in flight (the ledger
// list's payment_processing, the server's one derivation). The dialog names the payment as the server holds it (not
// cleared), the carrier's endorsement and the client's own address, asks the reason (required, logged) and sends through the
// automatic send's own path: when the payment clears, nothing is sent again. Owner and producers only, on their own sign-in;
// anyone else sees why not. What went is read back from document_sends.
// Review fix, round 2: "Add others (CC)" (Q21: every send), copied only when the mail went; and when the automatic claim is
// ALREADY HELD (sent early, or the early mail failed: the server's auto_claim) the dialog says so and offers the plain "Send
// endorsement" to send it again, the one way to resend while the payment is in flight (the clearing then sends nothing, so
// the plain send cannot make a second copy).
//
// @param {object} props
// @param {string} props.dealId         the deal (CRM deal id)
// @param {string} props.transactionId  the endorsement's policy_transactions row
// @param {string} props.clientName     the client as a person reads it (the named insured), never an id
// @param {Function} [props.onSent]     called with the send's answer (read back)
function SendNowDialog({ dealId, transactionId, clientName, open, onOpenChange, onSent }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState('');
  const [cc, setCc] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = useCallback(async () => {
    setError(null);
    try { setPreview(await getSendBeforeClearing(transactionId)); }
    catch (e) { setPreview(null); setError(e instanceof ApiError ? e.body?.error || e.code : e.message); }
  }, [transactionId]);
  useEffect(() => { if (open) { setResult(null); setReason(''); setCc(''); load(); } }, [open, load]);

  const line = preview?.line;
  const client = line?.client;
  const who = client?.label || clientName;
  const held = preview?.auto_claim || null;
  async function handleSend() {
    if (!reason.trim()) { toast.info('Say why: the reason is required and is logged.'); return; }
    setBusy(true);
    try {
      const out = await sendBeforeClearing(transactionId, { reason: reason.trim(), documentId: line?.document?.id, cc: ccList(cc) });
      setResult(out);
      if (out.delivered) toast.success(`${line.document.name} sent to ${out.sent_to} before the payment cleared. It will not be sent again when the payment clears.`);
      else toast.info(`${line.document.name}: ${sendWord(out.send)}`);
      announceDocumentsChanged(dealId);
      if (typeof onSent === 'function') onSent(out);
    } catch (e) {
      const body = e instanceof ApiError ? e.body : null;
      if (body?.auto_claim) setPreview((p) => ({ ...(p || {}), ...(body.preview ? { line: body.preview } : {}), auto_claim: body.auto_claim, eligible: false }));
      else if (body?.send) setResult(body);
      if (body?.preview && !body?.auto_claim) setPreview((p) => ({ ...(p || {}), line: body.preview }));
      toast.error(`Not sent: ${body?.error || e.message}`);
    } finally { setBusy(false); }
  }

  const canSend = preview?.can_send === true && preview?.eligible === true && !!reason.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-send-before-clearing>
        <DialogHeader>
          <DialogTitle>Send the endorsement to {clientName || who} before the payment clears</DialogTitle>
          <DialogDescription>For an urgent case. The carrier's endorsement goes now, attached, to {who} at their own email address, and is not sent again when the payment clears. The reason is required and is logged with your name.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {!preview && !error ? <p className="text-sm text-text-muted">Reading the endorsement and its payment...</p> : null}
          {error ? <p className="text-sm text-warning">{error}</p> : null}
          {preview ? (
            <>
              <p className="text-[13px] text-text" data-payment-words>{preview.payment?.words || preview.why || 'The payment is not in flight.'}</p>
              {!preview.payment?.processing && preview.why ? <p className="text-[13px] text-warning">{preview.why}</p> : null}
              {line?.document ? <ul><DocumentLine doc={{ ...line.document, earlier_sends: line.sends }} /></ul> : null}
              {line ? <p className="text-[12px] text-text-muted">To: {client?.address || 'no email address'}</p> : null}
              {line?.missing?.length ? <ul className="space-y-1.5">{line.missing.map((m) => <MissingItem key={m.action} item={m} ctx={{ dealId, client }} onDone={load} />)}</ul> : null}
              {held ? (
                <div className="rounded-card border border-border p-2 text-[13px]" data-send-now-held>
                  <p className="text-text">It already went once, as the one automatic send ({sendWord(held)}). When the payment clears it is not sent again.</p>
                  <p className="mt-1 text-text-muted">To send it to {who} again now:</p>
                  <div className="mt-1.5"><SendEndorsementButton dealId={dealId} transactionId={transactionId} clientName={clientName || who} onSent={() => { load(); if (typeof onSent === 'function') onSent(); }} /></div>
                </div>
              ) : null}
              {preview.auto_claim_checked === false ? <p className="text-[13px] text-warning">Could not check whether it already went once; the server still checks before it sends.</p> : null}
              {!held && preview.can_send === false ? <p className="text-[13px] text-warning" data-cannot-send>{preview.can_send_why}</p> : null}
              {preview.can_send && preview.eligible && !result ? (
                <>
                  <label className="block text-[13px] text-text">Why it cannot wait for the payment to clear
                    <textarea className="mt-1 w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-send-now-reason autoFocus />
                  </label>
                  {client?.address ? <CcField value={cc} onChange={setCc} to={who || client.address} disabled={busy} /> : null}
                </>
              ) : null}
            </>
          ) : null}
          {result ? <div className="rounded-card border border-border p-2 text-[13px]" data-send-result><p>{result.send ? sendWord(result.send) : 'Nothing was recorded'}</p><CcResult out={result} to={who} /></div> : null}
        </div>
        <DialogFooter className="sm:flex-wrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && !held ? (
            <Button variant="accent" className="h-auto min-h-9 whitespace-normal" disabled={busy || !canSend} onClick={handleSend} data-send-now>
              <Send size={14} strokeWidth={2} />{busy ? 'Sending...' : `Send the endorsement to ${who} now`}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function SendBeforeClearingButton({ dealId, transactionId, clientName, onSent }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant="outline" size="sm" data-deal-id={dealId} data-transaction-id={transactionId} disabled={!transactionId} onClick={() => setOpen(true)}>
        Payment processing: send now
      </Button>
      {open ? <SendNowDialog dealId={dealId} transactionId={transactionId} clientName={clientName} open={open} onOpenChange={setOpen} onSent={onSent} /> : null}
    </>
  );
}
